import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Card, Badge, Text, Group, Stack } from '@mantine/core';
import type { Item } from '../types';
import { ITEM_STATUS_LABELS, ITEM_STATUS_COLORS } from '../constants/status';
import { categoriesApi } from '../api/categories';
import {
  itemCard,
  itemCardImageWrapper,
  itemCardImage,
  itemCardPlaceholder,
} from '../styles/theme.css';
import { getSemester, daysAgoLabel } from '../utils/semester';


interface ItemCardProps {
  item: Item;
}

export function ItemCard({ item }: ItemCardProps) {
  const navigate = useNavigate();
  const firstImage = item.cover_image;
  const { data: catData } = useQuery({ queryKey: ['categories'], queryFn: categoriesApi.get, staleTime: Infinity });
  const categoryLabel = catData?.categories.find((c) => c.id === item.category_id)?.label_da ?? item.category_id;

  return (
    <Card
      className={itemCard}
      shadow="sm"
      padding={0}
      radius="md"
      withBorder
      onClick={() => navigate(`/items/${item.id}`)}
    >
      <div className={itemCardImageWrapper}>
        {firstImage ? (
          <img src={firstImage} alt={item.title} className={itemCardImage} />
        ) : (
          <div className={itemCardPlaceholder}>📦</div>
        )}
      </div>

      <Stack p="sm" gap={4}>
        <Group justify="space-between" wrap="nowrap">
          <Text fw={600} size="sm" lineClamp={1}>
            {item.title}
          </Text>
          <Badge size="xs" color={ITEM_STATUS_COLORS[item.status]} variant="light" tt="none">
            {ITEM_STATUS_LABELS[item.status]}
          </Badge>
        </Group>

        <Text size="xs" c="dimmed">
          {[categoryLabel, item.color, item.brand].filter(Boolean).join(' · ')}
        </Text>

        <Group gap={6}>
          <Badge size="xs" variant="light" color="blue" tt="none">
            {getSemester(item.found_date)}
          </Badge>
          <Text size="xs" c="dimmed">
            {daysAgoLabel(item.found_date)}
          </Text>
        </Group>

        {item.storage_location && (
          <Text size="xs" c="dimmed">
            📍 {item.storage_location}
          </Text>
        )}
      </Stack>
    </Card>
  );
}
